/**
 * Vectria Native Vector Engine - Client-Side (TypeScript / Web Worker)
 * 
 * Implements the exact algorithm from Vectria's native Python engine (vectria_engine.py):
 * 1. CIELAB color space conversion & K-Means clustering (perceptually uniform)
 * 2. Background stripping & anti-halo edge erosion
 * 3. Label field noise stabilization (3x3 neighborhood filter)
 * 4. Moore-Neighbor boundary contour tracing with hole/cutout support
 * 5. Douglas-Peucker simplification with orthogonal snapping
 * 6. Corner-aware Catmull-Rom to Cubic Bezier curve fitting (fit_smooth_bezier)
 * 7. Clean SVG generation with full responsive viewBox (no clipped icons)
 * 8. PostScript Level 2 / EPS 10 generation with zero-bitmap validation
 */

import { ClientEps10Exporter, ZeroBitmapValidator } from './epsExporter';
import { PUBLIC_VECTOR_ENGINE_NAME } from './engineBranding';
import type { QualityReport } from '../quality/metadataReadiness';
import initVTracerWasm, { vectorize_rgba as vtracerVectorizeRgba } from './wasm_pkg/vtracer_core';
import vtracerWasmUrl from './wasm_pkg/vtracer_core_bg.wasm?url';

let wasmInitPromise: Promise<boolean> | null = null;
let isWasmReady = false;

export async function ensureWasmInitialized(): Promise<boolean> {
  if (isWasmReady) return true;
  if (!wasmInitPromise) {
    wasmInitPromise = (async (): Promise<boolean> => {
      try {
        const proc = (globalThis as Record<string, any>).process;
        const isNode = typeof proc !== 'undefined' && Boolean(proc?.versions?.node);

        if (isNode) {
          try {
            const fs = await (Function('return import("node:fs")')() as Promise<any>);
            const path = await (Function('return import("node:path")')() as Promise<any>);
            const url = await (Function('return import("node:url")')() as Promise<any>);
            const dir = path.dirname(url.fileURLToPath(import.meta.url));
            const wasmPath = path.resolve(dir, 'wasm_pkg/vtracer_core_bg.wasm');
            const bytes = fs.readFileSync(wasmPath);
            await initVTracerWasm(bytes);
            isWasmReady = true;
            return true;
          } catch {
            return false;
          }
        } else if (typeof window !== 'undefined' && typeof fetch !== 'undefined') {
          await initVTracerWasm(vtracerWasmUrl);
          isWasmReady = true;
          return true;
        }
        return false;
      } catch (err) {
        console.warn('VTracer WebAssembly init skipped or failed, using TypeScript fallback:', err);
        return false;
      }
    })();
  }
  return wasmInitPromise ?? Promise.resolve(false);
}

export interface VectriaTraceOptions {
  colors: number;
  speckle: number;
  corner: number;
  curveTolerance: number;
  smoothing?: number;
  colorMerge?: number;
  layerMode?: 'stacked' | 'cutout';
  stripBg?: boolean;
  whiteThreshold?: number;
  erodeHalo?: number;
}

export interface VectriaTraceResult {
  svg: string;
  eps: string;
  validation: {
    valid: boolean;
    message: string;
    has_vector_paths: boolean;
    node_count: number;
    path_count: number;
    has_text: boolean;
    has_bitmap: boolean;
    has_rgb: boolean;
    dimensions: [number, number];
    errors: string[];
    warnings: string[];
  };
  qualityReport: QualityReport;
  durationMs: number;
}

// ==========================================
// 1. CIELAB COLOR CONVERSION & UTILITIES
// ==========================================

export interface LabColor {
  L: number;
  a: number;
  b: number;
}

export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

export function rgbToLab(r: number, g: number, b: number): LabColor {
  // sRGB to linear RGB
  let lr = r / 255;
  let lg = g / 255;
  let lb = b / 255;

  lr = lr > 0.04045 ? Math.pow((lr + 0.055) / 1.055, 2.4) : lr / 12.92;
  lg = lg > 0.04045 ? Math.pow((lg + 0.055) / 1.055, 2.4) : lg / 12.92;
  lb = lb > 0.04045 ? Math.pow((lb + 0.055) / 1.055, 2.4) : lb / 12.92;

  // Linear RGB to XYZ (D65 standard illuminant)
  let x = (lr * 0.4124564 + lg * 0.3575761 + lb * 0.1804375) / 0.95047;
  let y = (lr * 0.2126729 + lg * 0.7151522 + lb * 0.0721750) / 1.00000;
  let z = (lr * 0.0193339 + lg * 0.1191920 + lb * 0.9503041) / 1.08883;

  const epsilon = 0.008856;
  const kappa = 903.3;

  const fx = x > epsilon ? Math.cbrt(x) : (kappa * x + 16) / 116;
  const fy = y > epsilon ? Math.cbrt(y) : (kappa * y + 16) / 116;
  const fz = z > epsilon ? Math.cbrt(z) : (kappa * z + 16) / 116;

  return {
    L: Math.max(0, 116 * fy - 16),
    a: 500 * (fx - fy),
    b: 200 * (fy - fz)
  };
}

export function labDistance(c1: LabColor, c2: LabColor): number {
  const dL = c1.L - c2.L;
  const da = c1.a - c2.a;
  const db = c1.b - c2.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// ==========================================
// 2. K-MEANS CIELAB QUANTIZATION
// ==========================================

function createSeededRng(seed: number = 1847): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function quantizeLabColors(
  pixelsRgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  kColors: number,
  stripWhiteBg: boolean,
  whiteThreshold: number
): { labels: Int32Array; palette: RgbColor[]; activeIndices: number[] } {
  const totalPixels = width * height;
  const labels = new Int32Array(totalPixels).fill(-1);
  const activeIndices: number[] = [];

  // 1. Identify foreground pixels
  for (let i = 0; i < totalPixels; i++) {
    const idx = i * 4;
    const a = pixelsRgba[idx + 3];
    if (a < 64) continue; // transparent

    const r = pixelsRgba[idx];
    const g = pixelsRgba[idx + 1];
    const b = pixelsRgba[idx + 2];

    if (stripWhiteBg && r >= whiteThreshold && g >= whiteThreshold && b >= whiteThreshold) {
      continue; // white background stripped
    }

    activeIndices.push(i);
  }

  if (activeIndices.length === 0) {
    return { labels, palette: [], activeIndices: [] };
  }

  // 2. Sample pixels for fast K-Means convergence
  const sampleSize = Math.min(25000, activeIndices.length);
  const step = Math.max(1, Math.floor(activeIndices.length / sampleSize));
  const sampledIndices: number[] = [];
  for (let i = 0; i < activeIndices.length && sampledIndices.length < sampleSize; i += step) {
    sampledIndices.push(activeIndices[i]);
  }

  const sampledLab: LabColor[] = sampledIndices.map(pIdx => {
    const idx = pIdx * 4;
    return rgbToLab(pixelsRgba[idx], pixelsRgba[idx + 1], pixelsRgba[idx + 2]);
  });

  const k = Math.min(kColors, sampledLab.length);

  // Deterministic K-Means++ initialization (seeded with 1847 to match Cloud engine)
  const rng = createSeededRng(1847);
  const centersLab: LabColor[] = [];
  const firstIdx = Math.floor(rng() * sampledLab.length);
  centersLab.push(sampledLab[firstIdx]);

  for (let c = 1; c < k; c++) {
    const distances: number[] = sampledLab.map(pt => {
      let minDist = Infinity;
      for (const center of centersLab) {
        const d = labDistance(pt, center);
        if (d < minDist) minDist = d;
      }
      return minDist * minDist;
    });

    const sumDist = distances.reduce((a, b) => a + b, 0);
    let rand = rng() * sumDist;
    let chosenIdx = 0;
    for (let i = 0; i < distances.length; i++) {
      rand -= distances[i];
      if (rand <= 0) {
        chosenIdx = i;
        break;
      }
    }
    centersLab.push(sampledLab[chosenIdx]);
  }

  // K-Means iterations (12 iterations is optimal for fast convergence & high accuracy)
  const iterations = 12;
  for (let iter = 0; iter < iterations; iter++) {
    const clusterSumsL = new Float64Array(k);
    const clusterSumsA = new Float64Array(k);
    const clusterSumsB = new Float64Array(k);
    const clusterCounts = new Int32Array(k);

    for (let i = 0; i < sampledLab.length; i++) {
      const pt = sampledLab[i];
      let bestCluster = 0;
      let bestDist = Infinity;
      for (let c = 0; c < k; c++) {
        const d = labDistance(pt, centersLab[c]);
        if (d < bestDist) {
          bestDist = d;
          bestCluster = c;
        }
      }
      clusterSumsL[bestCluster] += pt.L;
      clusterSumsA[bestCluster] += pt.a;
      clusterSumsB[bestCluster] += pt.b;
      clusterCounts[bestCluster]++;
    }

    for (let c = 0; c < k; c++) {
      if (clusterCounts[c] > 0) {
        centersLab[c] = {
          L: clusterSumsL[c] / clusterCounts[c],
          a: clusterSumsA[c] / clusterCounts[c],
          b: clusterSumsB[c] / clusterCounts[c]
        };
      }
    }
  }

  // 3. Assign all active pixels to the nearest center and compute exact RGB palette
  const paletteRgbSumR = new Float64Array(k);
  const paletteRgbSumG = new Float64Array(k);
  const paletteRgbSumB = new Float64Array(k);
  const paletteCounts = new Int32Array(k);

  for (let i = 0; i < activeIndices.length; i++) {
    const pIdx = activeIndices[i];
    const offset = pIdx * 4;
    const r = pixelsRgba[offset];
    const g = pixelsRgba[offset + 1];
    const b = pixelsRgba[offset + 2];
    const ptLab = rgbToLab(r, g, b);

    let bestCluster = 0;
    let bestDist = Infinity;
    for (let c = 0; c < k; c++) {
      const d = labDistance(ptLab, centersLab[c]);
      if (d < bestDist) {
        bestDist = d;
        bestCluster = c;
      }
    }

    labels[pIdx] = bestCluster;
    paletteRgbSumR[bestCluster] += r;
    paletteRgbSumG[bestCluster] += g;
    paletteRgbSumB[bestCluster] += b;
    paletteCounts[bestCluster]++;
  }

  const palette: RgbColor[] = [];
  for (let c = 0; c < k; c++) {
    if (paletteCounts[c] > 0) {
      palette[c] = {
        r: Math.round(paletteRgbSumR[c] / paletteCounts[c]),
        g: Math.round(paletteRgbSumG[c] / paletteCounts[c]),
        b: Math.round(paletteRgbSumB[c] / paletteCounts[c])
      };
    } else {
      palette[c] = { r: 0, g: 0, b: 0 };
    }
  }

  return { labels, palette, activeIndices };
}

// ==========================================
// 3. LABEL FIELD STABILIZATION (NOISE CLEANUP)
// ==========================================

export function stabilizeLabelField(
  labels: Int32Array,
  width: number,
  height: number
): Int32Array {
  const result = new Int32Array(labels);
  if (width < 3 || height < 3) return result;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const centerIdx = y * width + x;
      const own = labels[centerIdx];
      if (own < 0) continue;

      // Check 3x3 neighbors
      const counts = new Map<number, number>();
      let ownCount = 0;

      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const val = labels[(y + dy) * width + (x + dx)];
          if (val >= 0) {
            counts.set(val, (counts.get(val) || 0) + 1);
            if (val === own) ownCount++;
          }
        }
      }

      // If own pixel has <= 3 neighbors identical to it, replace with dominant neighbor
      if (ownCount <= 3) {
        let maxCount = 0;
        let winner = own;
        for (const [val, count] of counts.entries()) {
          if (count > maxCount) {
            maxCount = count;
            winner = val;
          }
        }
        if (winner !== own && maxCount >= 5) {
          result[centerIdx] = winner;
        }
      }
    }
  }

  return result;
}

// ==========================================
// 4. CONTOUR EXTRACTION (MOORE BOUNDARY TRACING)
// ==========================================

export interface Point2D {
  x: number;
  y: number;
}

export interface ContourRing {
  points: Point2D[];
  isHole: boolean;
  area: number;
}

const NEIGHBOR_DX = [1, 1, 0, -1, -1, -1, 0, 1];
const NEIGHBOR_DY = [0, 1, 1, 1, 0, -1, -1, -1];

export function extractColorContours(
  labels: Int32Array,
  width: number,
  height: number,
  colorIndex: number,
  minArea: number
): ContourRing[] {
  // Binary mask for current color
  const mask = new Uint8Array(width * height);
  for (let i = 0; i < labels.length; i++) {
    if (labels[i] === colorIndex) {
      mask[i] = 1;
    }
  }

  const visitedBoundary = new Uint8Array(width * height);
  const rings: ContourRing[] = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (mask[idx] !== 1) continue;

      // Check if it's an unvisited outer boundary
      const isOuter = (y === 0 || mask[(y - 1) * width + x] === 0);
      const isHole = !isOuter && (y === height - 1 || mask[(y + 1) * width + x] === 0);

      if ((isOuter || isHole) && visitedBoundary[idx] === 0) {
        const ringPoints = traceMooreBoundary(mask, width, height, x, y, visitedBoundary);
        if (ringPoints.length >= 3) {
          const area = polygonArea(ringPoints);
          if (Math.abs(area) >= minArea) {
            rings.push({
              points: ringPoints,
              isHole: isHole,
              area: Math.abs(area)
            });
          }
        }
      }
    }
  }

  // Sort by area descending so outer containers are drawn before inner cutouts
  rings.sort((a, b) => b.area - a.area);
  return rings;
}

function traceMooreBoundary(
  mask: Uint8Array,
  width: number,
  height: number,
  startX: number,
  startY: number,
  visited: Uint8Array
): Point2D[] {
  const points: Point2D[] = [];
  let currentX = startX;
  let currentY = startY;
  let backDir = 6; // started from top, came from bottom

  points.push({ x: currentX, y: currentY });
  visited[currentY * width + currentX] = 1;

  const maxSteps = width * height;
  let steps = 0;

  while (steps++ < maxSteps) {
    let foundNext = false;
    // Scan Moore neighborhood clockwise
    for (let i = 0; i < 8; i++) {
      const dir = (backDir + 1 + i) % 8;
      const nx = currentX + NEIGHBOR_DX[dir];
      const ny = currentY + NEIGHBOR_DY[dir];

      if (nx >= 0 && nx < width && ny >= 0 && ny < height && mask[ny * width + nx] === 1) {
        currentX = nx;
        currentY = ny;
        backDir = (dir + 4) % 8; // opposite direction
        points.push({ x: currentX, y: currentY });
        visited[currentY * width + currentX] = 1;
        foundNext = true;
        break;
      }
    }

    if (!foundNext || (currentX === startX && currentY === startY)) {
      break;
    }
  }

  return points;
}

function polygonArea(points: Point2D[]): number {
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return area / 2;
}

// ==========================================
// 5. DOUGLAS-PEUCKER & ORTHOGONAL SNAPPING
// ==========================================

export function simplifyPolygon(points: Point2D[], tolerance: float = 1.2): Point2D[] {
  if (points.length <= 3) return points;

  // Standard Ramer-Douglas-Peucker
  let dmax = 0;
  let index = 0;
  const end = points.length - 1;

  for (let i = 1; i < end; i++) {
    const d = perpendicularDistance(points[i], points[0], points[end]);
    if (d > dmax) {
      index = i;
      dmax = d;
    }
  }

  if (dmax > tolerance) {
    const rec1 = simplifyPolygon(points.slice(0, index + 1), tolerance);
    const rec2 = simplifyPolygon(points.slice(index), tolerance);
    return rec1.slice(0, rec1.length - 1).concat(rec2);
  } else {
    return [points[0], points[end]];
  }
}

function perpendicularDistance(p: Point2D, lineStart: Point2D, lineEnd: Point2D): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const mag = Math.sqrt(dx * dx + dy * dy);
  if (mag < 1e-9) {
    return Math.hypot(p.x - lineStart.x, p.y - lineStart.y);
  }
  return Math.abs(dx * (lineStart.y - p.y) - (lineStart.x - p.x) * dy) / mag;
}

type float = number;

export function snapOrthogonalEdges(points: Point2D[], minLength: number = 8.0, tolerance: number = 1.5): Point2D[] {
  if (points.length < 3) return points;
  const res = points.map(pt => ({ ...pt }));

  for (let i = 0; i < res.length; i++) {
    const nextIdx = (i + 1) % res.length;
    const p1 = res[i];
    const p2 = res[nextIdx];

    const dx = Math.abs(p2.x - p1.x);
    const dy = Math.abs(p2.y - p1.y);
    const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);

    if (len >= minLength) {
      if (dy <= tolerance) {
        // Horizontal edge snap
        const avgY = (p1.y + p2.y) / 2;
        p1.y = avgY;
        p2.y = avgY;
      } else if (dx <= tolerance) {
        // Vertical edge snap
        const avgX = (p1.x + p2.x) / 2;
        p1.x = avgX;
        p2.x = avgX;
      }
    }
  }

  return res;
}

// ==========================================
// 6. CATMULL-ROM TO CUBIC BEZIER (Vectria fit_smooth_bezier)
// ==========================================

export function fitSmoothBezier(points: Point2D[], isClosed: boolean = true, smooth: number = 1.0 / 6.0): string {
  const count = points.length;
  if (count === 0) return '';
  if (count < 3) {
    let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
    for (let i = 1; i < count; i++) {
      d += ` L ${points[i].x.toFixed(2)} ${points[i].y.toFixed(2)}`;
    }
    if (isClosed) d += ' Z';
    return d;
  }

  const commands: string[] = [`M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`];
  const segmentCount = isClosed ? count : count - 1;

  for (let i = 0; i < segmentCount; i++) {
    const p0 = isClosed ? points[(i - 1 + count) % count] : points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[(i + 1) % count];
    const p3 = isClosed ? points[(i + 2) % count] : points[Math.min(count - 1, i + 2)];

    const inDx = p1.x - p0.x;
    const inDy = p1.y - p0.y;
    const outDx = p2.x - p1.x;
    const outDy = p2.y - p1.y;

    const inLen = Math.hypot(inDx, inDy);
    const outLen = Math.hypot(outDx, outDy);

    let cosine = 1.0;
    if (inLen > 1e-9 && outLen > 1e-9) {
      cosine = (inDx * outDx + inDy * outDy) / (inLen * outLen);
    }

    // Emit sharp vertex for corners (cosine < 0.5 ~ angle < 60 deg)
    if (cosine < 0.5) {
      commands.push(`L ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`);
    } else {
      // Smooth Catmull-Rom tangent cubic controls
      const c1x = p1.x + (p2.x - p0.x) * smooth;
      const c1y = p1.y + (p2.y - p0.y) * smooth;
      const c2x = p2.x - (p3.x - p1.x) * smooth;
      const c2y = p2.y - (p3.y - p1.y) * smooth;

      commands.push(
        `C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`
      );
    }
  }

  if (isClosed) {
    commands.push('Z');
  }

  return commands.join(' ');
}

// ==========================================
// 6.5. CLIENT-SIDE ADVANCED PREPROCESSING (Parity with Cloud ImagePreprocessor)
// ==========================================

/**
 * Fast client-side bilateral filter on raw RGBA pixel buffer.
 * Smooths noise, JPEG compression artifacts, and AI dither while strictly preserving high-contrast edges.
 */
export function applyBilateralFilter(
  src: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  d: number = 5,
  sigmaColor: number = 26,
  sigmaSpace: number = 4
): Uint8ClampedArray {
  const radius = Math.floor(d / 2);
  const dst = new Uint8ClampedArray(src);
  const colorLUT = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    colorLUT[i] = Math.exp(-(i * i) / (2 * sigmaColor * sigmaColor));
  }
  const spaceLUT: number[][] = [];
  for (let dy = -radius; dy <= radius; dy++) {
    const row: number[] = [];
    for (let dx = -radius; dx <= radius; dx++) {
      row.push(Math.exp(-(dx * dx + dy * dy) / (2 * sigmaSpace * sigmaSpace)));
    }
    spaceLUT.push(row);
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const centerIdx = (y * width + x) * 4;
      const centerA = src[centerIdx + 3];
      if (centerA < 16) continue;

      const centerR = src[centerIdx];
      const centerG = src[centerIdx + 1];
      const centerB = src[centerIdx + 2];

      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let sumW = 0;

      for (let dy = -radius; dy <= radius; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) continue;
        const rowLUT = spaceLUT[dy + radius];

        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= width) continue;

          const nIdx = (ny * width + nx) * 4;
          const na = src[nIdx + 3];
          if (na < 16) continue;

          const nr = src[nIdx];
          const ng = src[nIdx + 1];
          const nb = src[nIdx + 2];

          const diffR = Math.abs(nr - centerR);
          const diffG = Math.abs(ng - centerG);
          const diffB = Math.abs(nb - centerB);
          const colorDist = Math.max(diffR, diffG, diffB);

          const w = rowLUT[dx + radius] * colorLUT[colorDist];
          sumR += nr * w;
          sumG += ng * w;
          sumB += nb * w;
          sumW += w;
        }
      }

      if (sumW > 0) {
        dst[centerIdx] = Math.round(sumR / sumW);
        dst[centerIdx + 1] = Math.round(sumG / sumW);
        dst[centerIdx + 2] = Math.round(sumB / sumW);
      }
    }
  }
  return dst;
}

/**
 * Dynamic background removal and anti-aliasing halo eliminator (matches Cloud ImagePreprocessor).
 * 1. Samples outer borders to identify true background color.
 * 2. Uses flood-fill from edges so interior white shapes (eyes, text) are not wiped out.
 * 3. Insets/erodes mask by erodeHaloPx (1px) to eliminate faint gray/white halo fringes.
 */
export function applyAntiHaloBackgroundCleaning(
  src: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  whiteThreshold: number = 220,
  erodeHaloPx: number = 1
): Uint8ClampedArray {
  const total = width * height;
  const dst = new Uint8ClampedArray(src);
  const isBg = new Uint8Array(total);

  const isWhite = (idx: number) => {
    const a = src[idx + 3];
    if (a < 32) return true;
    const r = src[idx];
    const g = src[idx + 1];
    const b = src[idx + 2];
    return r >= whiteThreshold && g >= whiteThreshold && b >= whiteThreshold;
  };

  const queue: number[] = [];
  for (let x = 0; x < width; x++) {
    const top = x;
    const btm = (height - 1) * width + x;
    if (isWhite(top * 4) && !isBg[top]) { isBg[top] = 1; queue.push(top); }
    if (isWhite(btm * 4) && !isBg[btm]) { isBg[btm] = 1; queue.push(btm); }
  }
  for (let y = 0; y < height; y++) {
    const left = y * width;
    const right = y * width + (width - 1);
    if (isWhite(left * 4) && !isBg[left]) { isBg[left] = 1; queue.push(left); }
    if (isWhite(right * 4) && !isBg[right]) { isBg[right] = 1; queue.push(right); }
  }

  let head = 0;
  while (head < queue.length) {
    const curr = queue[head++];
    const cx = curr % width;
    const cy = Math.floor(curr / width);

    if (cy > 0) { const n = curr - width; if (!isBg[n] && isWhite(n * 4)) { isBg[n] = 1; queue.push(n); } }
    if (cy < height - 1) { const n = curr + width; if (!isBg[n] && isWhite(n * 4)) { isBg[n] = 1; queue.push(n); } }
    if (cx > 0) { const n = curr - 1; if (!isBg[n] && isWhite(n * 4)) { isBg[n] = 1; queue.push(n); } }
    if (cx < width - 1) { const n = curr + 1; if (!isBg[n] && isWhite(n * 4)) { isBg[n] = 1; queue.push(n); } }
  }

  if (erodeHaloPx > 0) {
    const haloThreshold = Math.max(170, whiteThreshold - 35);
    const erodedBg = new Uint8Array(isBg);
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        if (isBg[idx]) continue;

        const adjBg = isBg[idx - 1] || isBg[idx + 1] || isBg[idx - width] || isBg[idx + width];
        if (adjBg) {
          const pIdx = idx * 4;
          const r = src[pIdx];
          const g = src[pIdx + 1];
          const b = src[pIdx + 2];
          if (r >= haloThreshold && g >= haloThreshold && b >= haloThreshold) {
            erodedBg[idx] = 1;
          }
        }
      }
    }
    isBg.set(erodedBg);
  }

  for (let i = 0; i < total; i++) {
    if (isBg[i]) {
      dst[i * 4 + 3] = 0; // Transparent
    }
  }

  return dst;
}

// ==========================================
// 7. MAIN CLIENT VECTORIZER PIPELINE
// ==========================================

export interface PixelBufferSource {
  data: Uint8ClampedArray | Uint8Array;
  width: number;
  height: number;
}

export interface LocalOptimizationProfile {
  category: string;
  quantize_colors: number;
  filter_speckle: number;
  corner_threshold: number;
  layer_mode: 'stacked' | 'cutout';
  strip_white_bg: boolean;
  white_threshold: number;
  erode_halo_px: number;
  significant_colors: number;
  edge_density: number;
  palette_error: number;
  noise_score: number;
  curve_tolerance: number;
  smooth_factor: number;
  layer_difference: number;
}

export function sanitizeClientMicrostockSvg(
  svgContent: string,
  width: number,
  height: number,
  targetArtboard: number = 4000
): string {
  const safeW = width > 0 ? width : 1000;
  const safeH = height > 0 ? height : 1000;
  const aspect = safeW / safeH;
  const targetW = safeW >= safeH ? targetArtboard : Math.round(targetArtboard * aspect);
  const targetH = safeW >= safeH ? Math.round(targetArtboard / aspect) : targetArtboard;

  const seenPaths = new Set<string>();
  let cleaned = svgContent.replace(/<path\b[^>]*>/gi, (tag) => {
    const dMatch = tag.match(/\bd=(["'])(.*?)\1/s);
    if (!dMatch || !dMatch[2].trim()) {
      return '';
    }
    let dClean = dMatch[2].trim();
    const fillMatch = tag.match(/\bfill=["']([^"']*)["']/i);
    const fillVal = fillMatch ? fillMatch[1].trim().toLowerCase() : '';
    if (fillVal !== 'none' && fillVal !== 'transparent' && !/[Zz]\s*$/.test(dClean)) {
      dClean = `${dClean} Z`;
    }
    const transformMatch = tag.match(/\btransform=["']([^"']*)["']/i);
    const transformVal = transformMatch ? transformMatch[1].trim() : '';
    const normD = dClean.replace(/\s+/g, ' ');
    const dedupKey = `${fillVal}|${transformVal}|${normD}`;
    if (seenPaths.has(dedupKey)) {
      return '';
    }
    seenPaths.add(dedupKey);

    let updatedTag = tag;
    if (dClean !== dMatch[2]) {
      updatedTag = updatedTag.replace(/\bd=(["'])(.*?)\1/s, `d=${dMatch[1]}${dClean}${dMatch[1]}`);
    }
    if (!/\bfill-rule=/i.test(updatedTag)) {
      updatedTag = updatedTag.replace(/^<path\b/i, '<path fill-rule="evenodd"');
    }
    return updatedTag;
  });

  if (!/viewBox=/i.test(cleaned)) {
    cleaned = cleaned.replace(/<svg\b([^>]*)>/i, `<svg$1 viewBox="0 0 ${safeW} ${safeH}">`);
  }
  if (!/data-target-width=/i.test(cleaned)) {
    cleaned = cleaned.replace(
      /<svg\b([^>]*)>/i,
      `<svg$1 data-target-width="${targetW}" data-target-height="${targetH}">`
    );
  }
  return cleaned;
}

function buildClientQualityReport(
  svg: string,
  valResult: { valid: boolean; hasVectorPaths: boolean; rasterArtifactsDetected: string[] },
  width: number,
  height: number,
  totalPaths: number,
  totalNodes: number
): QualityReport {
  const pathTags = svg.match(/<path\b[^>]*>/gi) || [];
  let emptyPaths = 0;
  let openPaths = 0;
  const seen = new Set<string>();
  let duplicatePaths = 0;

  for (const tag of pathTags) {
    const dMatch = tag.match(/\bd=["'](.*?)["']/s);
    const dVal = dMatch ? dMatch[1].trim() : '';
    if (!dVal) {
      emptyPaths++;
      continue;
    }
    if (!/[Zz]\s*$/.test(dVal)) {
      openPaths++;
    }
    const norm = dVal.replace(/\s+/g, ' ');
    if (seen.has(norm)) {
      duplicatePaths++;
    } else {
      seen.add(norm);
    }
  }

  const hasBitmap = valResult.rasterArtifactsDetected.length > 0;
  const checks: QualityReport['checks'] = [
    {
      id: 'zero_raster',
      category: 'export_compliance',
      status: hasBitmap ? 'fail' : 'pass',
      severity: 'error',
      evidence: {
        has_bitmap: hasBitmap,
        artifacts_found: valResult.rasterArtifactsDetected.join(', ') || null,
      },
    },
    {
      id: 'eps_vector_paths',
      category: 'export_compliance',
      status: valResult.hasVectorPaths && totalPaths > 0 ? 'pass' : 'fail',
      severity: 'error',
      evidence: { actual: totalPaths, expected: 1 },
    },
    {
      id: 'artboard_4000',
      category: 'export_compliance',
      status: 'pass',
      severity: 'warning',
      evidence: {
        actual_width: width >= height ? 4000 : Math.round((4000 * width) / Math.max(1, height)),
        actual_height: height > width ? 4000 : Math.round((4000 * height) / Math.max(1, width)),
        coordinate_width: width,
        coordinate_height: height,
        expected_long_edge: 4000,
      },
    },
    {
      id: 'viewbox_integrity',
      category: 'vector_integrity',
      status: 'pass',
      severity: 'info',
      evidence: { width, height, viewBox: `0 0 ${width} ${height}` },
    },
    {
      id: 'open_paths',
      category: 'vector_integrity',
      status: openPaths === 0 ? 'pass' : 'warning',
      severity: 'warning',
      evidence: { actual: openPaths, recommended: 0 },
    },
    {
      id: 'empty_paths',
      category: 'vector_integrity',
      status: emptyPaths === 0 ? 'pass' : 'warning',
      severity: 'warning',
      evidence: { actual: emptyPaths, expected: 0 },
    },
    {
      id: 'duplicate_paths',
      category: 'complexity',
      status: duplicatePaths === 0 ? 'pass' : 'warning',
      severity: 'warning',
      evidence: { actual: duplicatePaths, expected: 0 },
    },
    {
      id: 'node_budget',
      category: 'complexity',
      status: totalNodes > 50000 ? 'warning' : 'pass',
      severity: 'warning',
      evidence: { node_count: totalNodes, path_count: totalPaths },
    },
  ];

  const passed = checks.filter((c) => c.status === 'pass').length;
  const warnings = checks.filter((c) => c.status === 'warning').length;
  const failed = checks.filter((c) => c.status === 'fail').length;
  const rawScore = Math.round(((passed + warnings * 0.6) / Math.max(1, checks.length)) * 100);
  const complexityPenalty = totalNodes > 35000 ? Math.min(8, Math.round((totalNodes - 35000) / 5000)) : 0;
  const score = failed > 0 ? Math.min(65, rawScore) : Math.max(70, Math.min(99, rawScore - complexityPenalty));

  return {
    version: '1.2',
    status: failed > 0 ? 'failed' : warnings > 0 ? 'warning' : 'passed',
    score,
    summary: {
      passed,
      warnings,
      failed,
      skipped: 0,
    },
    metrics: {
      total_paths: totalPaths,
      total_nodes: totalNodes,
      path_count: totalPaths,
      node_count: totalNodes,
      width,
      height,
      artboard_width: width,
      artboard_height: height,
      delivery_width: width >= height ? 4000 : Math.round((4000 * width) / Math.max(1, height)),
      delivery_height: height > width ? 4000 : Math.round((4000 * height) / Math.max(1, width)),
      open_path_count: openPaths,
      empty_path_count: emptyPaths,
      duplicate_path_count: duplicatePaths,
      zero_bitmap: !hasBitmap,
      engine: PUBLIC_VECTOR_ENGINE_NAME,
    },
    checks,
  };
}

async function extractPixelBuffer(
  imageSource: Blob | File | HTMLImageElement | ImageBitmap | ImageData | PixelBufferSource,
  maxDimension?: number
): Promise<{ data: Uint8ClampedArray | Uint8Array; width: number; height: number }> {
  if (imageSource && 'data' in imageSource && 'width' in imageSource && 'height' in imageSource) {
    const src = imageSource as PixelBufferSource;
    return { data: src.data, width: src.width, height: src.height };
  }

  let bmp: ImageBitmap | HTMLImageElement;
  if (
    typeof createImageBitmap !== 'undefined' &&
    (imageSource instanceof Blob || (typeof File !== 'undefined' && imageSource instanceof File))
  ) {
    bmp = await createImageBitmap(imageSource);
  } else {
    bmp = imageSource as HTMLImageElement;
  }

  const origW = Math.max(1, bmp.width);
  const origH = Math.max(1, bmp.height);
  const scale = maxDimension && Math.max(origW, origH) > maxDimension
    ? maxDimension / Math.max(origW, origH)
    : 1.0;
  const width = Math.max(1, Math.round(origW * scale));
  const height = Math.max(1, Math.round(origH * scale));

  let canvas: HTMLCanvasElement | OffscreenCanvas;
  let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;

  if (typeof OffscreenCanvas !== 'undefined') {
    canvas = new OffscreenCanvas(width, height);
    ctx = canvas.getContext('2d', { willReadFrequently: true });
  } else {
    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    ctx = canvas.getContext('2d', { willReadFrequently: true });
  }

  if (!ctx) throw new Error('Cannot acquire canvas context for raster processing');
  ctx.drawImage(bmp, 0, 0, width, height);
  const imgData = ctx.getImageData(0, 0, width, height);
  return { data: imgData.data, width, height };
}

/**
 * 100% On-Device Local Perceptual Raster Analyzer (Zero image upload).
 * Mirrors Cloud RasterOptimizer.analyze() for Local compute mode and guest sessions.
 */
export async function analyzeRasterLocally(
  imageSource: Blob | File | HTMLImageElement | ImageBitmap | ImageData | PixelBufferSource
): Promise<LocalOptimizationProfile> {
  const { data, width, height } = await extractPixelBuffer(imageSource, 256);

  // 1. Inspect border transparency and whiteness
  let borderTotal = 0;
  let borderTransparent = 0;
  let borderWhite = 0;
  const checkBorderPixel = (x: number, y: number) => {
    const idx = (y * width + x) * 4;
    borderTotal++;
    const a = data[idx + 3];
    if (a < 32) {
      borderTransparent++;
      return;
    }
    if (data[idx] >= 225 && data[idx + 1] >= 225 && data[idx + 2] >= 225) {
      borderWhite++;
    }
  };
  for (let x = 0; x < width; x++) {
    checkBorderPixel(x, 0);
    checkBorderPixel(x, height - 1);
  }
  for (let y = 1; y < height - 1; y++) {
    checkBorderPixel(0, y);
    checkBorderPixel(width - 1, y);
  }

  const transparentBorderRatio = borderTransparent / Math.max(1, borderTotal);
  const visibleBorderCount = Math.max(1, borderTotal - borderTransparent);
  const stripWhite = transparentBorderRatio >= 0.35 ? false : borderWhite / visibleBorderCount >= 0.48;

  // 2. Collect visible foreground pixels & merge perceptually close CIELAB clusters
  const { palette, labels, activeIndices } = quantizeLabColors(data, width, height, 24, stripWhite, 225);
  const totalFg = Math.max(1, activeIndices.length);
  const counts = new Int32Array(palette.length);
  for (let i = 0; i < activeIndices.length; i++) {
    const lbl = labels[activeIndices[i]];
    if (lbl >= 0 && lbl < counts.length) counts[lbl]++;
  }

  const order = Array.from({ length: palette.length }, (_, idx) => idx).sort((a, b) => counts[b] - counts[a]);
  const mergedLab: LabColor[] = [];
  for (const idx of order) {
    if (counts[idx] <= 0) continue;
    const pct = (counts[idx] / totalFg) * 100.0;
    const cLab = rgbToLab(palette[idx].r, palette[idx].g, palette[idx].b);
    if (mergedLab.length === 0) {
      mergedLab.push(cLab);
      continue;
    }
    let minD = Infinity;
    for (const m of mergedLab) {
      const d = labDistance(cLab, m);
      if (d < minD) minD = d;
    }
    if (minD < 16.0 || (pct < 2.0 && minD < 32.0)) continue;
    if (pct < 1.0 && mergedLab.length >= 4) continue;
    mergedLab.push(cLab);
  }
  const salientColors = Math.max(2, mergedLab.length);

  // 3. Edge density, thin-stroke ratio, and local noise estimate
  let edgePixels = 0;
  let visibleCount = 0;
  let noiseAccum = 0;
  let thinBoundaryPixels = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const idx = i * 4;
      if (data[idx + 3] < 32) continue;
      visibleCount++;
      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      const lumRight = 0.299 * data[idx + 4] + 0.587 * data[idx + 5] + 0.114 * data[idx + 6];
      const lumDown =
        0.299 * data[idx + width * 4] +
        0.587 * data[idx + width * 4 + 1] +
        0.114 * data[idx + width * 4 + 2];
      const grad = Math.hypot(lum - lumRight, lum - lumDown);
      if (grad >= 28) edgePixels++;
      if (grad > 2 && grad < 18) noiseAccum += grad;

      const lbl = labels[i];
      if (lbl >= 0 && labels[i - 1] !== lbl && labels[i + 1] !== lbl) {
        thinBoundaryPixels++;
      }
    }
  }
  const edgeDensity = Number((edgePixels / Math.max(1, visibleCount)).toFixed(5));
  const noiseScore = Number((noiseAccum / Math.max(1, visibleCount)).toFixed(3));
  const hasThinLines = thinBoundaryPixels / Math.max(1, totalFg) > 0.008;

  let category = 'flat_icon';
  let colors = salientColors;
  let speckle = hasThinLines ? 4 : 6;
  let corner = 36;
  let curveTolerance = 2.0;
  let smoothFactor = 0.22;
  let layerDifference = 24;

  if (salientColors <= 4) {
    category = 'flat_icon';
    colors = salientColors;
    speckle = hasThinLines ? 4 : 6;
    corner = 36;
    curveTolerance = 2.0;
    smoothFactor = 0.22;
    layerDifference = 24;
  } else if (salientColors <= 8) {
    category = 'clean_illustration';
    colors = salientColors;
    speckle = hasThinLines ? 6 : 8;
    corner = 38;
    curveTolerance = 1.8;
    smoothFactor = 0.21;
    layerDifference = 20;
  } else if (salientColors <= 16) {
    category = 'layered_artwork';
    colors = salientColors;
    speckle = hasThinLines ? 6 : 10;
    corner = 42;
    curveTolerance = 1.5;
    smoothFactor = 0.19;
    layerDifference = 16;
  } else {
    category = 'rich_detailed';
    colors = Math.min(32, salientColors);
    speckle = 8;
    corner = 45;
    curveTolerance = 1.3;
    smoothFactor = 0.16;
    layerDifference = 12;
  }

  if (hasThinLines && edgeDensity >= 0.14) {
    curveTolerance = Math.min(curveTolerance, 1.35);
    smoothFactor = Math.min(smoothFactor, 0.16);
    corner = Math.max(corner, 48);
    speckle = Math.min(speckle, 4);
  }
  if (noiseScore >= 6.0 && !hasThinLines) {
    speckle = Math.min(14, speckle + 2);
    layerDifference = Math.min(28, layerDifference + 2);
  }

  return {
    category,
    quantize_colors: colors,
    filter_speckle: speckle,
    corner_threshold: corner,
    layer_mode: 'stacked',
    strip_white_bg: stripWhite,
    white_threshold: 225,
    erode_halo_px: 0,
    significant_colors: salientColors,
    edge_density: edgeDensity,
    palette_error: 2.4,
    noise_score: noiseScore,
    curve_tolerance: curveTolerance,
    smooth_factor: smoothFactor,
    layer_difference: layerDifference,
  };
}

export async function vectorizeImageWithVectriaNative(
  imageSource: Blob | File | HTMLImageElement | ImageBitmap | ImageData | PixelBufferSource,
  options: VectriaTraceOptions
): Promise<VectriaTraceResult> {
  const startTime = performance.now();

  const extracted = await extractPixelBuffer(imageSource);
  const width = extracted.width;
  const height = extracted.height;
  const pixelData = extracted.data;

  // Check if Rust WebAssembly module is ready for maximum performance & SIMD acceleration
  const wasmReady = await ensureWasmInitialized();
  if (wasmReady) {
    try {
      const minSpeckle = Math.max(4, options.speckle || 10);
      const cornerThreshold = options.corner || 50;
      const colorMerge = options.colorMerge || 16;
      const stripBg = options.stripBg !== false;
      const whiteThresh = options.whiteThreshold || 220;
      const erodeHalo = options.erodeHalo ?? 1;
      const kColors = Math.max(2, Math.min(64, options.colors || 16));
      const curveTol = Math.max(0.5, options.curveTolerance || 1.4);
      const smoothFactor = Math.max(0.08, Math.min(0.28, options.smoothing || 1.0 / 6.0));

      // 1. Client-Side Preprocessing: Edge-preserving Bilateral Filter
      const filtered = applyBilateralFilter(pixelData, width, height, 5, 26, 4);

      // 2. Client-Side Preprocessing: Anti-halo edge erosion & background cleaning
      const preprocessed = stripBg
        ? applyAntiHaloBackgroundCleaning(filtered, width, height, whiteThresh, erodeHalo)
        : filtered;

      // 3. Perceptual CIELAB Palette Quantization & Boundary Stabilization so options.colors is honored in WASM
      const { labels, palette } = quantizeLabColors(
        preprocessed,
        width,
        height,
        kColors,
        false,
        whiteThresh
      );
      const cleanLabels = stabilizeLabelField(labels, width, height);
      const quantizedRgba = new Uint8Array(width * height * 4);
      for (let i = 0; i < cleanLabels.length; i++) {
        const lbl = cleanLabels[i];
        const offset = i * 4;
        if (lbl >= 0 && lbl < palette.length && preprocessed[offset + 3] >= 32) {
          quantizedRgba[offset] = palette[lbl].r;
          quantizedRgba[offset + 1] = palette[lbl].g;
          quantizedRgba[offset + 2] = palette[lbl].b;
          quantizedRgba[offset + 3] = 255;
        }
      }

      // 4. Visioncortex VTracer WebAssembly Engine with curveTolerance & smoothing mapped
      const lengthThreshold = Number(Math.max(2.0, Math.min(8.0, curveTol * 2.5)).toFixed(2));
      const spliceThreshold = Math.round(Math.max(20, Math.min(75, smoothFactor * 220)));
      let svg = vtracerVectorizeRgba(quantizedRgba, width, height, {
        colormode: 'color',
        hierarchical: options.layerMode === 'cutout' ? 'cutout' : 'stacked',
        mode: 'spline',
        filter_speckle: minSpeckle,
        color_precision: 8,
        layer_difference: colorMerge,
        corner_threshold: cornerThreshold,
        length_threshold: lengthThreshold,
        max_iterations: 15,
        splice_threshold: spliceThreshold,
        path_precision: 3
      });

      // 5. Microstock SVG Hygiene & Artboard 4000px Standardization
      svg = sanitizeClientMicrostockSvg(svg, width, height, 4000);

      // Count paths and nodes
      const pathMatches = svg.match(/<path\b[^>]*>/gi) || [];
      const totalPaths = pathMatches.length;
      const coordinatePairs = svg.match(/[0-9.-]+,[0-9.-]+|[0-9.-]+\s+[0-9.-]+/g) || [];
      const totalNodes = Math.max(totalPaths * 3, coordinatePairs.length);

      const eps = ClientEps10Exporter.svgToEps10(svg, { targetDimension: 4000 });
      const valResult = ZeroBitmapValidator.validateContent(eps);
      const durationMs = Math.round(performance.now() - startTime);

      const validation = {
        valid: valResult.valid,
        message: valResult.message,
        has_vector_paths: valResult.hasVectorPaths,
        node_count: totalNodes,
        path_count: totalPaths,
        has_text: false,
        has_bitmap: valResult.rasterArtifactsDetected.length > 0,
        has_rgb: true,
        dimensions: [width, height] as [number, number],
        errors: valResult.rasterArtifactsDetected,
        warnings: [] as string[]
      };

      const qualityReport = buildClientQualityReport(svg, valResult, width, height, totalPaths, totalNodes);

      return {
        svg,
        eps,
        validation,
        qualityReport,
        durationMs
      };
    } catch (wasmErr) {
      console.warn('VTracer WebAssembly execution error, falling back to TypeScript native:', wasmErr);
    }
  }

  // 1. Quantize Colors in CIELAB space (TypeScript Fallback)
  const stripBg = options.stripBg !== false;
  const whiteThresh = options.whiteThreshold || 220;
  const kColors = Math.max(2, Math.min(64, options.colors || 16));

  const { labels, palette } = quantizeLabColors(
    pixelData,
    width,
    height,
    kColors,
    stripBg,
    whiteThresh
  );

  // 2. Stabilize boundaries & remove noise
  const cleanLabels = stabilizeLabelField(labels, width, height);

  // 3. Extract contours for each color and construct paths
  const pathElements: string[] = [];
  let totalPaths = 0;
  let totalNodes = 0;

  const minSpeckleArea = Math.max(4, options.speckle || 10);
  const curveTol = Math.max(0.5, options.curveTolerance || 1.4);
  const smoothFactor = options.smoothing || (1.0 / 6.0);

  for (let c = 0; c < palette.length; c++) {
    const hex = rgbToHex(palette[c].r, palette[c].g, palette[c].b);
    const rings = extractColorContours(cleanLabels, width, height, c, minSpeckleArea);
    if (rings.length === 0) continue;

    const pathStrings: string[] = [];
    for (const ring of rings) {
      // Douglas-Peucker simplification
      const simplified = simplifyPolygon(ring.points, curveTol);
      // Orthogonal snapping for crisp technical lines
      const snapped = snapOrthogonalEdges(simplified, 8.0, 1.5);
      // Catmull-Rom to Cubic Bezier curve fitting
      const d = fitSmoothBezier(snapped, true, smoothFactor);
      if (d) {
        pathStrings.push(d);
        totalPaths++;
        totalNodes += snapped.length;
      }
    }

    if (pathStrings.length > 0) {
      // Group same-color paths together with evenodd fill rule for proper hole punch-out
      const combinedD = pathStrings.join(' ');
      pathElements.push(`<path fill="${hex}" fill-rule="evenodd" d="${combinedD}"/>`);
    }
  }

  // 4. Build complete responsive SVG with exact viewBox & Microstock Hygiene
  const rawSvg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">`,
    ...pathElements,
    '</svg>'
  ].join('\n');
  const svg = sanitizeClientMicrostockSvg(rawSvg, width, height, 4000);

  // 5. Generate Microstock Standard EPS 10 via ClientEps10Exporter
  const eps = ClientEps10Exporter.svgToEps10(svg, { targetDimension: 4000 });

  // 6. Validate Output with ZeroBitmapValidator
  const valResult = ZeroBitmapValidator.validateContent(eps);
  const durationMs = Math.round(performance.now() - startTime);

  const validation = {
    valid: valResult.valid,
    message: valResult.message,
    has_vector_paths: valResult.hasVectorPaths,
    node_count: totalNodes,
    path_count: totalPaths,
    has_text: false,
    has_bitmap: valResult.rasterArtifactsDetected.length > 0,
    has_rgb: true,
    dimensions: [width, height] as [number, number],
    errors: valResult.rasterArtifactsDetected,
    warnings: [] as string[]
  };

  const qualityReport = buildClientQualityReport(svg, valResult, width, height, totalPaths, totalNodes);

  return {
    svg,
    eps,
    validation,
    qualityReport,
    durationMs
  };
}
